import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, UserGroupIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Team, TEAM_COLORS } from './SortableEntity';
import { RankedPlayer } from '../hooks/useTeamManagement';
import { Player, PlayerWithLeagueContext, LeagueOutput } from '../generated-api';
import { PlayerRatingInfo } from '../hooks/useRecentPlayers';
import PlayerSelectionGrid from './PlayerSelectionGrid';
import Button from './common/Button';

interface SelectPlayersCardProps {
  players: (Player | PlayerWithLeagueContext)[];
  selectedPlayerIds: string[];
  rankedPlayers: RankedPlayer[];
  teams: Team[];
  onTogglePlayerSelection: (playerId: string) => void;
  onRemovePlayerFromTeam: (playerId: string) => void;
  getPlayerTeam: (playerId: string) => Team | undefined;
  getTeamColor: (teamId: string) => any;
  addPlayerToTeam: (playerId: string, teamId: string) => void;
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>;
  setRankedPlayers: React.Dispatch<React.SetStateAction<RankedPlayer[]>>;
  addPlayerPath?: string;
  onBeforeNavigate?: () => void;
  leagues?: LeagueOutput[];
  recentPlayerIds?: string[];
  /** Map of player ID to their last play date (ISO string) */
  lastPlayedMap?: Record<string, string>;
  /** Map of player ID to their global league rating info */
  playerRatingsMap?: Record<string, PlayerRatingInfo>;
}

const SelectPlayersCard: React.FC<SelectPlayersCardProps> = ({
  players,
  selectedPlayerIds,
  rankedPlayers,
  teams,
  onTogglePlayerSelection,
  onRemovePlayerFromTeam,
  getPlayerTeam,
  getTeamColor,
  addPlayerToTeam,
  setTeams,
  setRankedPlayers,
  addPlayerPath = '/players/new',
  onBeforeNavigate,
  leagues = [],
  recentPlayerIds = [],
  lastPlayedMap = {},
  playerRatingsMap = {},
}) => {
  const navigate = useNavigate();
  const [isTeamEditMode, setIsTeamEditMode] = useState(false);

  const handleEnterTeamEditMode = () => {
    setIsTeamEditMode(true);
  };

  const handleCancelTeamEditMode = () => {
    setIsTeamEditMode(false);
  };

  // Handler for cycling through teams in edit mode
  const handleCyclePlayerTeam = (playerId: string) => {
    const playerTeam = getPlayerTeam(playerId);
    const MAX_TEAMS = 5;
    const teamColorsToUse = TEAM_COLORS.slice(0, MAX_TEAMS); // Use first 5 colors: Red, Blue, Yellow, Black, Pink
    
    if (!playerTeam) {
      // Player has no team - assign to first team (Red)
      const firstColor = teamColorsToUse[0];
      const existingTeam = teams.find(t => t.color === firstColor.name.toLowerCase());
      
      if (existingTeam) {
        // Team exists - add player to it
        addPlayerToTeam(playerId, existingTeam.id);
      } else {
        // Team doesn't exist - create it with the specific color
        const newTeamId = `team-${Date.now()}`;
        const newTeam: Team = {
          id: newTeamId,
          name: `Team ${firstColor.name}`,
          color: firstColor.name.toLowerCase(),
          playerIds: [playerId],
        };
        setTeams(prev => [...prev, newTeam]);
        setRankedPlayers(prev => 
          prev.map(p => p.id === playerId ? { ...p, teamId: newTeamId } : p)
        );
      }
    } else {
      // Player is in a team - find current team's color index
      const currentColorIndex = teamColorsToUse.findIndex(
        color => color.name.toLowerCase() === playerTeam.color
      );
      
      if (currentColorIndex === -1 || currentColorIndex === MAX_TEAMS - 1) {
        // Player is in last team or unknown team - remove from team
        onRemovePlayerFromTeam(playerId);
      } else {
        // Move to next team
        const nextColor = teamColorsToUse[currentColorIndex + 1];
        onRemovePlayerFromTeam(playerId);
        
        // Find or create the next team
        const nextTeam = teams.find(t => t.color === nextColor.name.toLowerCase());
        if (nextTeam) {
          addPlayerToTeam(playerId, nextTeam.id);
        } else {
          // Create new team with next color
          const newTeamId = `team-${Date.now()}`;
          const newTeam: Team = {
            id: newTeamId,
            name: `Team ${nextColor.name}`,
            color: nextColor.name.toLowerCase(),
            playerIds: [playerId],
          };
          setTeams(prev => [...prev, newTeam]);
          setRankedPlayers(prev => 
            prev.map(p => p.id === playerId ? { ...p, teamId: newTeamId } : p)
          );
        }
      }
    }
  };

  const handleAddPlayerClick = () => {
    if (onBeforeNavigate) {
      onBeforeNavigate();
    }
    navigate(addPlayerPath, { state: { returnPath: window.location.pathname } });
  };

  return (
    <PlayerSelectionGrid
      players={players}
      selectedPlayerIds={selectedPlayerIds}
      rankedPlayers={rankedPlayers}
      teams={teams}
      onTogglePlayerSelection={isTeamEditMode ? handleCyclePlayerTeam : onTogglePlayerSelection}
      onCreateTeam={() => {}}
      onAddPlayerToTeam={() => {}}
      onRemovePlayerFromTeam={onRemovePlayerFromTeam}
      getPlayerTeam={getPlayerTeam}
      getTeamColor={getTeamColor}
      isTeamEditMode={isTeamEditMode}
      playersSelectedForTeam={[]}
      leagues={leagues}
      recentPlayerIds={recentPlayerIds}
      lastPlayedMap={lastPlayedMap}
      playerRatingsMap={playerRatingsMap}
      headerAction={
        isTeamEditMode ? (
          <Button
            variant="success"
            size="md"
            onClick={handleCancelTeamEditMode}
            leftIcon={<CheckIcon className="h-4 w-4" />}
          >
            Done
          </Button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="primary"
              size="md"
              onClick={handleAddPlayerClick}
              leftIcon={<PlusIcon className="h-4 w-4" />}
              data-testid="new-player-button"
            >
              New Player
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleEnterTeamEditMode}
              leftIcon={<UserGroupIcon className="h-4 w-4" />}
              data-testid="edit-teams-button"
            >
              Edit Teams
            </Button>
          </div>
        )
      }
    />
  );
};

export default SelectPlayersCard;

